import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { useState, useEffect } from 'react';
import SlideShell from './SlideShell';
import MarkdownRenderer from './MarkdownRenderer';

interface TrueFalseItem {
  id: string;
  statement: string;
  answer: 'True' | 'False';
  explanation?: string;
}

interface TrueFalseSlideProps {
  slideNumber: number;
  totalSlides: number;
  title: string;
  subtitle: string;
  content: { items: TrueFalseItem[] };
  lessonTitle?: string;
  lessonThumbnail?: string;
  onNext?: () => void;
  onBack?: () => void;
  onBackToLesson?: () => void;
  isCompleted?: boolean;
  onMarkComplete?: () => void;
}

const TrueFalseSlide = ({
  slideNumber,
  totalSlides,
  title,
  subtitle,
  content,
  lessonTitle,
  lessonThumbnail,
  onNext,
  onBack,
  onBackToLesson,
  isCompleted,
  onMarkComplete,
}: TrueFalseSlideProps) => {
  const items = content.items || [];
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<'True' | 'False' | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [completedItems, setCompletedItems] = useState<Set<number>>(new Set());
  const [answers, setAnswers] = useState<Record<number, {
    selectedAnswer: 'True' | 'False' | null;
    showResult: boolean;
  }>>({});

  const currentItem = items[currentItemIndex];
  const isCorrect = selectedAnswer === currentItem?.answer;
  const allItemsCompleted = completedItems.size === items.length;

  const handleSubmit = (answer: 'True' | 'False') => {
    setSelectedAnswer(answer);
    setShowResult(true);
    setAnswers({
      ...answers,
      [currentItemIndex]: { selectedAnswer: answer, showResult: true }
    });
    if (answer === currentItem.answer) {
      setCompletedItems(prev => new Set(prev).add(currentItemIndex));
    }
  };

  const handleNextItem = () => {
    if (currentItemIndex < items.length - 1) {
      setCurrentItemIndex(prev => prev + 1);
    }
  };

  const handlePrevItem = () => {
    if (currentItemIndex > 0) {
      setCurrentItemIndex(prev => prev - 1);
    }
  };

  const handleResetCurrent = () => {
    setSelectedAnswer(null);
    setShowResult(false);
    const newAnswers = { ...answers };
    delete newAnswers[currentItemIndex];
    setAnswers(newAnswers);
    setCompletedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(currentItemIndex);
      return newSet;
    });
  };

  const handleResetAll = () => {
    setAnswers({});
    setSelectedAnswer(null);
    setShowResult(false);
    setCompletedItems(new Set());
  };

  // Load saved answer when index changes
  useEffect(() => {
    const saved = answers[currentItemIndex];
    setSelectedAnswer(saved?.selectedAnswer ?? null);
    setShowResult(saved?.showResult ?? false);
  }, [currentItemIndex, answers]);

  if (!currentItem) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-muted-foreground">No content available</p>
      </div>
    );
  }

  return (
    <SlideShell
      lessonTitle={lessonTitle}
      lessonThumbnail={lessonThumbnail}
      slideNumber={slideNumber}
      totalSlides={totalSlides}
      subtitle={subtitle}
      onBackToLesson={onBackToLesson}
      onBack={onBack}
      onNext={onNext}
      showMarkComplete={true}
      isCompleted={isCompleted}
      onMarkComplete={allItemsCompleted ? onMarkComplete : undefined}
    >
      <h1 className="font-heading text-2xl mb-6 text-foreground">
        {title}
      </h1>

      {/* Item counter and controls */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          Statement {currentItemIndex + 1} of {items.length}
        </p>
        <div className="flex gap-2">
          {showResult && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetCurrent}
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
          )}
          {Object.keys(answers).length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetAll}
            >
              Reset All
            </Button>
          )}
          {items.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrevItem}
                disabled={currentItemIndex === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNextItem}
                disabled={currentItemIndex === items.length - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-[15px] border border-[#f0f0f0] p-5 mb-6">
        <MarkdownRenderer content={currentItem.statement} />
      </div>

      <p className="text-sm font-medium mb-4 text-foreground">
        Is this statement true or false?
      </p>

      {!showResult ? (
        <div className="flex gap-4 mb-6">
          <Button
            onClick={() => handleSubmit('True')}
            className="flex-1 bg-white hover:bg-[#fafafa] text-foreground border border-[#f0f0f0] rounded-[15px] py-2.5 text-base"
          >
            True
          </Button>
          <Button
            onClick={() => handleSubmit('False')}
            className="flex-1 bg-white hover:bg-[#fafafa] text-foreground border border-[#f0f0f0] rounded-[15px] py-2.5 text-base"
          >
            False
          </Button>
        </div>
      ) : (
        <>
          <div className={`rounded-[15px] p-4 mb-4 ${isCorrect ? 'bg-[#27c840]/10' : 'bg-destructive/10'}`}>
            <p className="text-sm font-medium mb-1">
              {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
            </p>
            <p className="text-sm text-foreground">
              The statement is <span className="font-medium">{currentItem.answer}</span>
            </p>
          </div>

          {currentItem.explanation && (
            <div className="bg-white rounded-[15px] border border-[#f0f0f0] p-4 mb-6">
              <p className="text-sm font-medium mb-2">
                Explanation:
              </p>
              <MarkdownRenderer content={currentItem.explanation} />
            </div>
          )}
        </>
      )}
    </SlideShell>
  );
};

export default TrueFalseSlide;
